import { useState, type CSSProperties, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { SortableTh } from '@/shared/ui/SortableTh';
import { fetchCities, fetchCountries, fetchRegions } from '../users/api';
import { fetchReviewsList } from './api';

interface UserRef {
  id: number | string;
  name?: string;
  country?: { name?: string };
  region?: { name?: string };
  city?: { name?: string };
}

interface ReviewRow extends Record<string, unknown> {
  id: number | string;
  customer?: UserRef | null;
  service?: UserRef | null;
  create_time?: number | string;
  bid_id?: number | string;
  rating?: number | string;
  status_id?: number;
  review_new?: { type_id?: number; text?: string } | null;
}

const SEARCH_KEY_OPTIONS = [
  { value: 'id', label: 'Поиск по id отзыва' },
  { value: 'customer_id', label: 'Поиск по id клиента' },
  { value: 'service_id', label: 'Поиск по id сервиса' },
];

const RATING_OPTIONS = [0, 1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }));

const REVIEW_TEXT_COLUMNS = [
  { typeId: 1, label: 'Качество ремонта' },
  { typeId: 2, label: 'Скорость выполнения' },
  { typeId: 3, label: 'Клиентский сервис' },
  { typeId: 4, label: 'Соответствие ожиданиям' },
  { typeId: 5, label: 'Стоимость работ' },
  { typeId: 6, label: 'Другое' },
];

const emptyFilters = {
  searchKey: '',
  searchValue: '',
  countryId: '',
  regionId: '',
  cityId: '',
  rating: [] as string[],
};

type Filters = typeof emptyFilters;

const DEFAULT_SORT = '-id';
const RUSSIA_ID = '1';

const labelStyle: CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 4 };
const mutedHeadStyle: CSSProperties = { cursor: 'default' };

const toOptions = (items: Array<{ id: string | number; name?: string }> = []) =>
  items.map((item) => ({ value: String(item.id), label: String(item.name ?? item.id) }));

const UserCell = ({ user, empty }: { user?: UserRef | null; empty: string }) => (
  <td>
    {user ? <Link to={`/users/user/${user.id}`}>{user.name}</Link> : empty}
    <div className="id-customer-box">{user ? user.id : 'N/A'}</div>
  </td>
);

const formatLocation = (service?: UserRef | null) =>
  service
    ? [service.country?.name || 'No country', service.region?.name || 'No region', service.city?.name || 'No city'].join(', ')
    : 'No service data';

export const ReviewsPage = () => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(DEFAULT_SORT);
  const [draft, setDraft] = useState<Filters>({ ...emptyFilters, countryId: RUSSIA_ID });
  const [applied, setApplied] = useState<Filters>({ ...emptyFilters, countryId: RUSSIA_ID });

  const countriesQuery = useQuery({ queryKey: ['countries'], queryFn: fetchCountries });
  const regionsQuery = useQuery({
    queryKey: ['regions', draft.countryId],
    queryFn: () => fetchRegions(draft.countryId),
    enabled: Boolean(draft.countryId),
  });
  const citiesQuery = useQuery({
    queryKey: ['cities', draft.regionId],
    queryFn: () => fetchCities(draft.regionId),
    enabled: Boolean(draft.regionId),
  });

  const listParams = {
    page,
    sort: sortField,
    id: applied.searchKey === 'id' ? applied.searchValue : '',
    customer_id: applied.searchKey === 'customer_id' ? applied.searchValue : '',
    service_id: applied.searchKey === 'service_id' ? applied.searchValue : '',
    country_id: applied.countryId,
    region_id: applied.regionId,
    city_id: applied.cityId,
    rating: applied.rating,
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['reviews', listParams],
    queryFn: () => fetchReviewsList(listParams),
  });

  const rows = getListRows<ReviewRow>(data, ['items']);
  const totalPages = getTotalPages(data);

  const setDraftField = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSort = (field: string) => {
    setSortField((prev) => (prev === field ? `-${field}` : prev === `-${field}` ? field : `-${field}`));
  };

  const sortTh = (field: string, label: string) => (
    <SortableTh active={sortField === field || sortField === `-${field}`} onClick={() => toggleSort(field)}>
      {label}
    </SortableTh>
  );

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setApplied({ ...draft });
  };

  const onReset = () => {
    setDraft(emptyFilters);
    setApplied(emptyFilters);
    setPage(1);
  };

  return (
    <>
      <section className="content-header">
        <h1>Отзывы</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Список Отзывов</h3>
            <form onSubmit={onSearch} style={{ marginTop: 12 }}>
              <div className="row">
                <div className="col-lg-4">
                  <div className="form-group">
                    <span style={labelStyle}>Поиск</span>
                    <FilterSelect
                      options={SEARCH_KEY_OPTIONS}
                      value={draft.searchKey}
                      onChange={(searchKey) => setDraftField('searchKey', searchKey)}
                    />
                    <input
                      className="form-control input-sm"
                      style={{ marginTop: 6, width: 250 }}
                      placeholder="Поиск..."
                      value={draft.searchValue}
                      onChange={(e) => setDraftField('searchValue', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по стране</span>
                    <FilterSelect
                      options={toOptions(countriesQuery.data)}
                      value={draft.countryId}
                      isLoading={countriesQuery.isLoading}
                      onChange={(countryId) => setDraft((p) => ({ ...p, countryId, regionId: '', cityId: '' }))}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по региону</span>
                    <FilterSelect
                      options={toOptions(regionsQuery.data)}
                      value={draft.regionId}
                      isDisabled={!draft.countryId}
                      isLoading={regionsQuery.isFetching}
                      onChange={(regionId) => setDraft((p) => ({ ...p, regionId, cityId: '' }))}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по городу</span>
                    <FilterSelect
                      options={toOptions(citiesQuery.data)}
                      value={draft.cityId}
                      isDisabled={!draft.regionId}
                      isLoading={citiesQuery.isFetching}
                      onChange={(cityId) => setDraftField('cityId', cityId)}
                    />
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по рейтингу</span>
                    <MultiSelect
                      options={RATING_OPTIONS}
                      value={draft.rating}
                      onChange={(rating) => setDraftField('rating', rating)}
                    />
                  </div>
                </div>
              </div>
              <button type="button" className="btn btn-warning" onClick={onReset} disabled={isFetching}>
                Очистить все поля
              </button>{' '}
              <button type="submit" className="btn btn-primary" disabled={isFetching}>
                Найти
              </button>
            </form>
          </div>

          <div className="box-body">
            {isLoading && <p>Загрузка…</p>}
            {error && <div className="alert alert-danger">Ошибка загрузки отзывов</div>}
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    {sortTh('id', 'id отзыва')}
                    {sortTh('customer_id', 'Клиент')}
                    <th>Автосервис</th>
                    <th>Расположение сервиса</th>
                    {sortTh('create_time', 'Дата и время оставления отз.')}
                    {sortTh('bid_id', 'id заявки')}
                    {sortTh('rating', 'Рейтинг')}
                    {REVIEW_TEXT_COLUMNS.map(({ typeId, label }) => (
                      <th key={typeId} style={mutedHeadStyle}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={String(r.id)} className={r.status_id ? '' : 'bg-danger'}>
                      <td>{r.id}</td>
                      <UserCell user={r.customer} empty="No customer" />
                      <UserCell user={r.service} empty="No service" />
                      <td>{formatLocation(r.service)}</td>
                      <td>{r.create_time ? dayjs(Number(r.create_time) * 1000).format('DD.MM.YYYY HH:mm') : ''}</td>
                      <td>{r.bid_id || ''}</td>
                      <td>{Number(r.rating) || ''}</td>
                      {REVIEW_TEXT_COLUMNS.map(({ typeId }) => (
                        <td key={typeId} style={{ whiteSpace: 'normal' }}>
                          {r.review_new?.type_id === typeId ? r.review_new.text : ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
